import type { ItemNode, OrdersParseTree } from "../parser/types";
import type { BlankField, BlankFieldType } from "./types";

const BLANK = /_9C|_+/g;

interface LocatedItem {
  readonly groupId: string;
  readonly item: ItemNode;
}

function itemsInDeliveryOrder(tree: OrdersParseTree): readonly LocatedItem[] {
  return tree.paragraphs.flatMap((paragraph) => [
    ...paragraph.items.map((item) => ({ groupId: paragraph.id, item })),
    ...paragraph.subparagraphs.flatMap((subparagraph) =>
      subparagraph.items.map((item) => ({ groupId: paragraph.id, item })),
    ),
  ]);
}

function inferType(item: ItemNode, token: string): BlankFieldType {
  if (token === "_9C") return "callsign-prefix";
  if (item.type === "table") return "time";
  if (/chain of command|\bCoC\b/i.test(item.displayText)) return "callsign";
  if (/\b(?:SP|LD)\s+_+/i.test(item.displayText)) return "control-measure";
  if (/\bgrid\s+_+/i.test(item.displayText)) return "grid";
  if (/_+\s+Up\b/i.test(item.displayText)) return "formation";
  return "unknown";
}

function fieldsForItem(item: ItemNode, groupId: string): readonly BlankField[] {
  const fields: BlankField[] = [];
  for (const match of item.displayText.matchAll(BLANK)) {
    const sourceToken = match[0];
    const index = fields.length + 1;
    fields.push({
      id: `${item.id}/blank-${String(index).padStart(2, "0")}`,
      nodeId: item.id,
      groupId,
      sourceToken,
      length: sourceToken === "_9C" ? 1 : sourceToken.length,
      fieldType: inferType(item, sourceToken),
    });
  }

  if (item.type === "table" && /\|\s*$/.test(item.displayText)) {
    const index = fields.length + 1;
    fields.push({
      id: `${item.id}/blank-${String(index).padStart(2, "0")}`,
      nodeId: item.id,
      groupId,
      sourceToken: "",
      length: 0,
      fieldType: "time",
    });
  }
  return fields;
}

export function extractBlanks(tree: OrdersParseTree): readonly BlankField[] {
  return itemsInDeliveryOrder(tree).flatMap(({ groupId, item }) =>
    fieldsForItem(item, groupId),
  );
}
